'use client'

import Link from 'next/link'
import Image from 'next/image'
import {stegaClean} from '@sanity/client/stega'
import {useState} from 'react'
import {useIntl} from 'react-intl'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import type {ActionButton, MenuDocument, MenuItem} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'
import {DealerDrawerTrigger} from '@/components/layout/DealerDrawerTrigger'
import {isMegaMenuItemVisible, isMenuItemVisible, resolveMenuItemHref} from '@/lib/menu'
import {isDealerFinderAction} from '@/lib/dealerWidget'
import {sanitizeHtml} from '@/lib/sanitizeHtml'

type MegaMenuProps = {
  menu?: MenuDocument | null
  utilityMenu?: MenuDocument | null
  searchHref?: string
  extraButtons?: ActionButton[]
  extraHtml?: string
  site?: SiteLite | null
  /** Active page brand context — passed to the dealer drawer so it filters dealers by brand. */
  brandContext?: string | null
  /** JO2-246: with the new header layout on, "Hide in the mega menu" applies. */
  newHeaderLayout?: boolean
  /** JO2-246: the header's top bar is off, so list its links at the bottom of the desktop menu. */
  utilityLinksOnDesktop?: boolean
}

type MegaMenuColumnData = {
  key: string
  title?: {
    label: string
    href: string | null
    item: MenuItem
  }
  links: Array<{
    key: string
    label: string
    href: string
    item: MenuItem
  }>
}

const getVisibleChildren = (
  item: MenuItem | null | undefined,
  newHeaderLayout: boolean,
): MenuItem[] =>
  (item?.children ?? []).filter((child) => isMegaMenuItemVisible(child, newHeaderLayout))

const isExternalItem = (item: MenuItem | ActionButton) => item.linkType === 'external'

const linkRel = (item: {openInNewTab?: boolean; nofollow?: boolean}): string | undefined =>
  [item.openInNewTab ? 'noreferrer' : null, item.nofollow ? 'nofollow' : null]
    .filter(Boolean)
    .join(' ') || undefined

const buildMenuTreeColumns = (
  menu: MenuDocument | null | undefined,
  newHeaderLayout: boolean,
): MegaMenuColumnData[] => {
  const items = (menu?.items ?? []).filter((item) => isMegaMenuItemVisible(item, newHeaderLayout))
  return items
    .map((item, index) => {
      const childItems = getVisibleChildren(item, newHeaderLayout)
      const sourceItems = childItems.length > 0 ? childItems : [item]
      const links = sourceItems
        .map((source, childIndex) => {
          const href = resolveMenuItemHref(source)
          if (!href || !source.label) return null
          return {
            key: source._key || `${item._key}-${childIndex}`,
            label: source.label,
            href,
            item: source,
          }
        })
        .filter(Boolean) as MegaMenuColumnData['links']

      if (links.length < 1) return null

      return {
        key: item._key || `menu-column-${index}`,
        title:
          childItems.length > 0 && item.label
            ? {
                label: item.label,
                href: resolveMenuItemHref(item),
                item,
              }
            : undefined,
        links,
      }
    })
    .filter(Boolean) as MegaMenuColumnData[]
}

const LinkForItem = ({
  href,
  item,
  className,
  children,
}: {
  href: string
  item: MenuItem | ActionButton
  className: string
  children: React.ReactNode
}) => {
  const target = item.openInNewTab ? '_blank' : undefined
  const rel = linkRel(item)

  if (isExternalItem(item)) {
    return (
      <a href={href} target={target} rel={rel} className={className}>
        {children}
      </a>
    )
  }

  return (
    <Link href={href} target={target} rel={rel} className={className}>
      {children}
    </Link>
  )
}

export function MegaMenu({
  menu,
  utilityMenu,
  searchHref = '/search',
  extraButtons,
  extraHtml,
  site,
  brandContext,
  newHeaderLayout = false,
  utilityLinksOnDesktop = false,
}: MegaMenuProps) {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  // CMS-sourced HTML rendered via dangerouslySetInnerHTML — sanitize to prevent
  // stored XSS, matching the policy already applied to Footer and product bullets.
  const safeExtraHtml = extraHtml ? sanitizeHtml(extraHtml) : undefined
  const [openMobileSectionIndex, setOpenMobileSectionIndex] = useState<number | null>(0)
  const mainItems = (menu?.items ?? []).filter((item) =>
    isMegaMenuItemVisible(item, newHeaderLayout),
  )
  const utilityItems = (utilityMenu?.items ?? []).filter(isMenuItemVisible)
  const mainColumns = buildMenuTreeColumns(menu, newHeaderLayout)
  const columns = mainColumns.slice(0, 4)
  const resolvedExtraButtons = extraButtons ?? []

  return (
    <Sheet>
      <SheetTrigger
        id="mega-menu-sheet-trigger"
        render={
          <button
            type="button"
            aria-label={t('navigation.menu.open', 'Menu')}
            // py-1 keeps the touch target 28px tall (the icon is 20px); no
            // side padding, so the gaps to the icons beside it are even.
            className="cursor-pointer py-1"
          />
        }
      >
        <Image
          src="/icons/menu.svg"
          alt=""
          width={28}
          height={20}
          sizes="28px"
          className="h-5 w-7"
        />
      </SheetTrigger>

      <SheetContent
        id="mega-menu-sheet-content"
        side="top"
        showCloseButton={false}
        className="h-auto max-h-[90vh] w-full overflow-y-auto border-b-0 bg-card pb-site-section-sm pt-0 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
      >
        <div className="container container-padding">
          <SheetHeader className="flex flex-row items-start justify-between border-b-0 px-0 pb-site-md pt-site-md">
            <Link href="/" className="hidden h-[110px] w-[116px] items-end lg:flex">
              <Image
                src="/jotul-logo.svg"
                alt="Jøtul"
                width={116}
                height={110}
                sizes="116px"
                className="h-auto w-full"
              />
            </Link>
            <SheetTitle className="sr-only">{t('navigation.menu.title', 'Navigation')}</SheetTitle>

            <div className="flex flex-1 items-start justify-end gap-site-md">
              <form
                action={searchHref}
                method="get"
                className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-full border border-[#4B4F54] bg-card px-4 focus-within:ring-2 focus-within:ring-primary/40 focus-within:ring-offset-2 lg:max-w-[350px]"
              >
                <label htmlFor="mega-menu-search" className="sr-only">
                  {t('navigation.search.label', 'Search')}
                </label>
                <input
                  id="mega-menu-search"
                  name="q"
                  type="text"
                  placeholder={t('navigation.search.placeholder', 'Manuals, products, dealer...')}
                  className="min-w-0 flex-1 bg-transparent text-base text-foreground placeholder:text-[#4B4F54] focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:rounded-sm"
                />
                <button type="submit" aria-label={t('navigation.search.submit', 'Search')}>
                  <Image
                    src="/icons/search.svg"
                    alt=""
                    width={16}
                    height={16}
                    sizes="16px"
                    className="size-4"
                  />
                </button>
              </form>
              <SheetClose
                render={
                  <button
                    type="button"
                    aria-label={t('navigation.menu.close', 'Close menu')}
                    className="flex size-12 cursor-pointer items-center justify-center p-0 text-foreground lg:size-14"
                  />
                }
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="5" x2="23" y2="23" />
                  <line x1="23" y1="5" x2="5" y2="23" />
                </svg>
              </SheetClose>
            </div>
          </SheetHeader>

          <nav
            aria-label={t('navigation.menu.ariaLabel', 'Main menu')}
            className="space-y-site-md lg:hidden"
          >
            <div className="space-y-1">
              {mainItems.map((item, index) => {
                const href = resolveMenuItemHref(item)
                const childItems = getVisibleChildren(item, newHeaderLayout).filter((child) =>
                  Boolean(resolveMenuItemHref(child) && child.label),
                )

                if (childItems.length > 0) {
                  const isOpen = openMobileSectionIndex === index
                  const panelId = `mega-menu-mobile-panel-${item._key || index}`
                  return (
                    <div
                      key={item._key || `mobile-parent-${index}`}
                      className="border-b border-border/40"
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setOpenMobileSectionIndex(isOpen ? null : index)}
                        className="flex w-full cursor-pointer items-center justify-between py-3 text-left text-lg font-medium text-foreground"
                      >
                        <span>{item.label || t('navigation.menu.open', 'Menu')}</span>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          className={`text-primary-light transition-transform duration-200 ${
                            isOpen ? 'rotate-0' : 'rotate-180'
                          }`}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      <div
                        id={panelId}
                        className={`grid transition-all duration-300 ease-out ${
                          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="pb-2">
                            {childItems.map((child, childIndex) => {
                              const childHref = resolveMenuItemHref(child)
                              if (!childHref || !child.label) return null
                              return (
                                <LinkForItem
                                  key={child._key || `${item._key}-child-${childIndex}`}
                                  href={childHref}
                                  item={child}
                                  className="flex items-center gap-2 border-t border-border/20 py-3 pl-3 text-base font-medium text-foreground"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                    className="text-primary-light"
                                  >
                                    <polyline points="9 6 15 12 9 18" />
                                  </svg>
                                  <span>{child.label}</span>
                                </LinkForItem>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }

                if (!href) return null

                return (
                  <LinkForItem
                    key={item._key || `mobile-link-${index}`}
                    href={href}
                    item={item}
                    className="flex items-center justify-between border-b border-border/40 py-3 text-lg font-medium text-foreground"
                  >
                    <span>{item.label || t('navigation.menu.open', 'Menu')}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className="text-primary-light"
                    >
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </LinkForItem>
                )
              })}
            </div>
            {resolvedExtraButtons.length > 0 ? (
              <div className="flex flex-wrap gap-3 pt-2">
                {resolvedExtraButtons.slice(0, 2).map((button, index) => {
                  const href = resolveMenuItemHref(button)
                  const isSecondary = stegaClean(button.variant) === 'secondary'
                  const className = `inline-flex cursor-pointer rounded-full border px-5 py-2 text-sm font-semibold ${
                    isSecondary
                      ? 'border-border text-foreground'
                      : 'border-primary-light bg-primary-light text-white'
                  }`
                  if (isDealerFinderAction(href, site)) {
                    return (
                      <DealerDrawerTrigger
                        key={button._key || `mobile-action-${index}`}
                        site={site}
                        brandKeys={brandContext ? [brandContext] : undefined}
                        label={button.label || t('navigation.action.default', 'Action')}
                        className={className}
                        analytics={{
                          name: 'mega_menu_extra_button',
                          location: 'mega_menu_mobile',
                          variant: isSecondary ? 'secondary' : 'primary',
                        }}
                      />
                    )
                  }
                  if (!href) return null

                  return (
                    <LinkForItem
                      key={button._key || `mobile-action-${index}`}
                      href={href}
                      item={button}
                      className={className}
                    >
                      <span
                        data-analytics-cta-name="mega_menu_extra_button"
                        data-analytics-cta-location="mega_menu_mobile"
                        data-analytics-cta-text={
                          button.label || t('navigation.action.default', 'Action')
                        }
                        data-analytics-cta-variant={isSecondary ? 'secondary' : 'primary'}
                      >
                        {button.label || t('navigation.action.default', 'Action')}
                      </span>
                    </LinkForItem>
                  )
                })}
              </div>
            ) : null}
            {safeExtraHtml ? (
              <div
                className="text-sm text-muted-foreground [&_a]:underline [&_a:hover]:text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
                dangerouslySetInnerHTML={{__html: safeExtraHtml}}
              />
            ) : null}
            {utilityItems.length > 0 ? (
              <div className="space-y-2 border-t border-border/20 pt-4">
                {utilityItems.map((item, index) => {
                  const href = resolveMenuItemHref(item)
                  if (!href || !item.label) return null
                  return (
                    <LinkForItem
                      key={item._key || `mobile-utility-${index}`}
                      href={href}
                      item={item}
                      className="block text-sm font-medium text-foreground underline"
                    >
                      {item.label}
                    </LinkForItem>
                  )
                })}
              </div>
            ) : null}
          </nav>

          <nav aria-label={t('navigation.menu.ariaLabel', 'Main menu')} className="hidden lg:block">
            <div className="grid grid-cols-4 gap-x-12 gap-y-10 pb-10 pt-4">
              {columns.map((column) => (
                <section key={column.key} className="min-w-0">
                  {column.title ? (
                    column.title.href ? (
                      <LinkForItem
                        href={column.title.href}
                        item={column.title.item}
                        className="mb-5 block text-[24px] font-medium leading-[1.15] text-foreground hover:text-primary-light"
                      >
                        {column.title.label}
                      </LinkForItem>
                    ) : (
                      <h3 className="mb-5 text-[24px] font-medium leading-[1.15] text-foreground">
                        {column.title.label}
                      </h3>
                    )
                  ) : null}
                  <div className="space-y-4">
                    {column.links.map((link) => (
                      <LinkForItem
                        key={link.key}
                        href={link.href}
                        item={link.item}
                        className="block text-[18px] leading-[1.35] text-foreground hover:text-primary-light"
                      >
                        {link.label}
                      </LinkForItem>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {(safeExtraHtml || resolvedExtraButtons.length > 0) && (
              <div className="border-t border-border/20 pb-8 pt-6">
                {resolvedExtraButtons.length > 0 ? (
                  <div className="flex flex-wrap justify-end gap-3">
                    {resolvedExtraButtons.slice(0, 2).map((button, index) => {
                      const href = resolveMenuItemHref(button)
                      const isSecondary = stegaClean(button.variant) === 'secondary'
                      const className = `inline-flex min-w-[126px] cursor-pointer justify-center rounded-full border px-8 py-3 text-[18px] font-semibold ${
                        isSecondary
                          ? 'border-border text-foreground'
                          : 'border-primary-light bg-primary-light text-white'
                      }`
                      if (isDealerFinderAction(href, site)) {
                        return (
                          <DealerDrawerTrigger
                            key={button._key || `desktop-action-${index}`}
                            site={site}
                            brandKeys={brandContext ? [brandContext] : undefined}
                            label={button.label || t('navigation.action.default', 'Action')}
                            className={className}
                            analytics={{
                              name: 'mega_menu_extra_button',
                              location: 'mega_menu_desktop',
                              variant: isSecondary ? 'secondary' : 'primary',
                            }}
                          />
                        )
                      }
                      if (!href) return null

                      return (
                        <LinkForItem
                          key={button._key || `desktop-action-${index}`}
                          href={href}
                          item={button}
                          className={className}
                        >
                          <span
                            data-analytics-cta-name="mega_menu_extra_button"
                            data-analytics-cta-location="mega_menu_desktop"
                            data-analytics-cta-text={
                              button.label || t('navigation.action.default', 'Action')
                            }
                            data-analytics-cta-variant={isSecondary ? 'secondary' : 'primary'}
                          >
                            {button.label || t('navigation.action.default', 'Action')}
                          </span>
                        </LinkForItem>
                      )
                    })}
                  </div>
                ) : null}
                {safeExtraHtml ? (
                  <div className="mt-6 flex justify-end">
                    <div
                      className="max-w-xl text-sm text-muted-foreground [&_a]:underline [&_a:hover]:text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{__html: safeExtraHtml}}
                    />
                  </div>
                ) : null}
              </div>
            )}
            {/* JO2-246: with the header's top bar off, its links (Dealer login,
                Extranett) move here, at the bottom of the desktop menu. Phones
                already list them at the bottom of the mobile menu. */}
            {utilityLinksOnDesktop && utilityItems.length > 0 ? (
              <div className="flex flex-wrap justify-end gap-x-site-md gap-y-2 border-t border-border/20 pt-4">
                {utilityItems.map((item, index) => {
                  const href = resolveMenuItemHref(item)
                  if (!href || !item.label) return null
                  return (
                    <LinkForItem
                      key={item._key || `desktop-utility-${index}`}
                      href={href}
                      item={item}
                      className="text-sm font-medium text-foreground underline"
                    >
                      {item.label}
                    </LinkForItem>
                  )
                })}
              </div>
            ) : null}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  )
}
